// Calculate zero value period height based on time range and duration
export const getZeroValuePeriodHeight = (
  period: { start: number; end: number },
  customTimeRange: { start: Date; end: Date } | null,
) => {
  const duration = period.end - period.start;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const days = duration / MS_PER_DAY;

  // For custom time ranges, calculate height based on duration
  if (customTimeRange) {
    if (days <= 30) return 0.02; // Short duration
    if (days <= 90) return 0.05; // Medium duration
    return 0.08; // Long duration
  }

  // Default height for all time view
  return 0.12;
};

// Calculate chart width based on data range and window size
export const calculateChartWidth = (
  visibleTimeRange: {
    startDate: Date;
    endDate: Date;
    windowSize?: number;
    isCustomRange?: boolean;
    selectedRange?: {
      start: Date;
      end: Date;
    };
  } | null,
  customTimeRange: { start: Date; end: Date } | null,
): string => {
  if (!visibleTimeRange) return "100%";

  // For "all" view, show everything
  if (!customTimeRange) return "100%";

  const { startDate, endDate, windowSize, isCustomRange, selectedRange } =
    visibleTimeRange;

  // For custom ranges, calculate width based on total time range and selected window size
  if (isCustomRange && selectedRange && windowSize) {
    const totalRange = endDate.getTime() - startDate.getTime();
    const selectedDuration =
      selectedRange.end.getTime() - selectedRange.start.getTime();

    // Calculate width ratio based on the selected window size
    // Add padding (1.2) to ensure we have enough space for scrolling
    const scrollableWidth = Math.max(
      (totalRange / selectedDuration) * 100 * 1.2,
      100,
    );
    return `${scrollableWidth}%`;
  }

  return "100%";
};

// Calculate zero-value periods for overlays
export const getZeroValuePeriods = (
  activityChartData: Array<{ timestamp: number; decayed_in_value: number }>,
) => {
  if (!activityChartData.length) return [];

  interface Period {
    start: number;
    end: number;
  }

  const ZERO_THRESHOLD = 0.05;
  const periods: Period[] = [];

  // Find transitions between zero and non-zero values
  const isZero = (value: number) => value < ZERO_THRESHOLD;
  let currentPeriod: Period | null = null;

  activityChartData.forEach((point, index) => {
    const isCurrentZero = isZero(point.decayed_in_value);

    if (isCurrentZero && !currentPeriod) {
      // Start a new period
      currentPeriod = { start: point.timestamp, end: point.timestamp };
    } else if (!isCurrentZero && currentPeriod) {
      // End the current period at the previous point
      currentPeriod.end = activityChartData[index - 1].timestamp;
      periods.push(currentPeriod);
      currentPeriod = null;
    } else if (isCurrentZero && currentPeriod) {
      // Update the end time of the current period
      currentPeriod.end = point.timestamp;
    }
  });

  // Handle case where we end with a zero period
  if (currentPeriod) {
    periods.push(currentPeriod);
  }

  return periods;
};

// Generate custom ticks
export function generateCustomTicks(
  startDate: number,
  endDate: number,
  maximumNumberOfTicks: number = 30,
  visibleWindowSize?: number,
): number[] {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const totalDuration = endDate - startDate;
  const totalDays = totalDuration / MS_PER_DAY;

  // Determine step size in days based on the time range
  let stepDays: number;

  if (visibleWindowSize) {
    // If we have a visible window size, use that to determine step size
    const visibleDays = visibleWindowSize / MS_PER_DAY;
    stepDays = getStepSizeFromDays(visibleDays);
  } else {
    // Otherwise, use the total range and maximum number of ticks
    stepDays = Math.max(1, Math.ceil(totalDays / maximumNumberOfTicks));
    stepDays = getStepSizeFromDays(totalDays, stepDays);
  }

  // For quarterly or yearly ticks, align to natural boundaries
  if (stepDays >= 90) {
    return generateAlignedTicks(
      new Date(startDate),
      new Date(endDate),
      stepDays,
    );
  }

  // For daily, weekly, or monthly ticks, use simple interval-based ticks
  const result: number[] = [];
  const stepMs = stepDays * MS_PER_DAY;

  for (let current = startDate; current <= endDate; current += stepMs) {
    result.push(current);
  }

  return result;
}

// Helper function to generate ticks aligned to natural boundaries (quarters, years)
function generateAlignedTicks(
  startDate: Date,
  endDate: Date,
  stepDays: number,
): number[] {
  const result: number[] = [];
  const isYearly = stepDays >= 365;

  // Clone the start date to avoid modifying the original
  const current = new Date(startDate);

  // Align to the start of year for yearly ticks, or nearest quarter for quarterly
  current.setDate(1);

  if (isYearly) {
    // For yearly ticks, start at January
    current.setMonth(0);
  } else {
    // For quarterly ticks, align to nearest quarter (Jan, Apr, Jul, Oct)
    const month = current.getMonth();
    current.setMonth(Math.floor(month / 3) * 3);
  }

  // Generate ticks with appropriate steps
  while (current <= endDate) {
    result.push(current.getTime());

    if (isYearly) {
      current.setFullYear(current.getFullYear() + 1);
    } else {
      current.setMonth(current.getMonth() + 3); // Quarterly
    }
  }

  return result;
}

// Helper function to determine step size based on number of days
function getStepSizeFromDays(days: number, defaultStepDays?: number): number {
  if (days > 365 * 5) {
    return 365; // Yearly for ranges > 6 years
  } else if (days > 365 * 3) {
    return 180; // Semi-yearly for ranges > 4 years
  } else if (days > 365 * 1.5) {
    return 90; // Quarterly
  } else if (days > 365) {
    return 30; // Monthly
  } else if (days > 90) {
    return 14; // Bi-weekly
  } else if (days > 30) {
    return 7; // Weekly
  } else if (days > 14) {
    return 3; // Every 3 days
  } else if (days > 7) {
    return 2; // Every other day
  } else {
    return defaultStepDays || 1; // Daily (or use provided default)
  }
}

// Helper function to generate color shades based on stage progression using opacity
export function getStageColor(
  stageIndex: number,
  totalStages: number,
  outcome: "won" | "lost" | "active" = "active",
): string {
  // Calculate opacity based on stage index - start higher for better visibility
  const opacity = 0.4 + 0.6 * (stageIndex / Math.max(1, totalStages - 1));

  // Use CSS variables for colors
  const colorMap = {
    won: `hsl(var(--chart-4) / ${opacity.toFixed(2)})`, // Green
    lost: `hsl(var(--chart-1) / ${opacity.toFixed(2)})`, // Red
    active: `hsl(var(--muted-foreground) / ${opacity.toFixed(2)})`, // Gray
  };

  return colorMap[outcome];
}

// Calculate visible time range based on available data
export const getVisibleTimeRange = (
  timelineData: { activities: Array<{ date: string }> } | undefined,
  opportunities: Array<{ created_date: string }> | undefined,
  firstTouchpointData:
    | { first_touchpoints: Array<{ date: string }> }
    | undefined,
  customTimeRange: { start: Date; end: Date } | null,
) => {
  const allTimestamps = [
    ...(timelineData?.activities.map((a) => new Date(a.date).getTime()) ?? []),
    ...(opportunities?.map((o) => new Date(o.created_date).getTime()) ?? []),
    ...(firstTouchpointData?.first_touchpoints.map((t) =>
      new Date(t.date).getTime(),
    ) ?? []),
  ];

  if (allTimestamps.length === 0) return null;

  const minTimestamp = Math.min(...allTimestamps);
  const maxTimestamp = Math.max(
    Math.min(Date.now(), Math.max(...allTimestamps)),
  );

  // If we have a custom time range from drag selection, use that
  if (customTimeRange) {
    const rangeDuration =
      customTimeRange.end.getTime() - customTimeRange.start.getTime();

    return {
      startDate: new Date(minTimestamp), // Full range start
      endDate: new Date(maxTimestamp), // Full range end
      windowSize: rangeDuration, // Use the selected range duration as window size
      effectiveTimeRange: "all" as const,
      isCustomRange: true,
      selectedRange: {
        start: customTimeRange.start,
        end: customTimeRange.end,
      },
    };
  }

  // For "all" view, show everything
  return {
    startDate: new Date(minTimestamp),
    endDate: new Date(maxTimestamp),
  };
};
